import { catchError, filter, repeat, switchMap } from 'rxjs/operators';
import { first, map, of, tap } from 'rxjs';
import { TUI_IS_MOBILE_RES } from '@taiga-ui/core';
import { Store } from '@ngrx/store';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { createUserContextHeader, UserContextService, WO_CONTEXT_HEADER } from '@iss/libs/user-context';
import { CountriesConfigGQL, MyPermissionsGQL, OnlyOneCustomerGQL, OnlyOneSiteGQL } from '@iss/libs/graphql';
import { inject, Injectable } from '@angular/core';

import { selectShouldCheckOnlyOneCustomer, selectShouldCheckOnlyOneSite, selectShouldResetCountries } from './app-context.selectors';
import { appContextFeature } from './app-context.feature';
import { appContextActions } from './app-context.actions';

import { httpCode, mapApolloResult, mapApolloResultToActions } from '@iss/wo-common/utils';
import { authActions } from '@iss/wo-common/ngrx';

@Injectable()
export class AppContextEffects {
  private readonly userContextService = inject(UserContextService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly isMobileRes$ = inject(TUI_IS_MOBILE_RES);
  private readonly onlyOneCustomer = inject(OnlyOneCustomerGQL);
  private readonly onlyOneSite = inject(OnlyOneSiteGQL);
  private readonly myPermissionsGQL = inject(MyPermissionsGQL);
  private readonly countriesConfigGQL = inject(CountriesConfigGQL);

  public readonly fetchPermissions$ = createEffect(() => this.actions$.pipe(
    ofType(
      authActions.detailsLoaded,
    ),
    switchMap(() => this.myPermissionsGQL.watch().valueChanges),
    map(
      mapApolloResultToActions({
        loading: appContextActions.permissionsLoading(),
        data: ({ myAuthDetails }) => appContextActions.permissionsLoaded({ permissions: myAuthDetails.distinctPermissions }),
        error: () => appContextActions.permissionsLoadingFailure({}),
      }),
    ),
    catchError(err => of(appContextActions.permissionsLoadingFailure({ httpCode: httpCode(err) }))),
    repeat(),
  ));

  public readonly checkIfOnlyOneCustomerAvailable$ = createEffect(() => this.actions$.pipe(
    ofType(
      authActions.detailsLoaded,
      appContextActions.roleSelected,
    ),
    concatLatestFrom(() => [
      this.store.select(selectShouldCheckOnlyOneCustomer),
      this.store.select(appContextFeature.selectRole),
    ]),
    filter(([ , shouldCheckOnlyOneCustomer ]) => shouldCheckOnlyOneCustomer),
    switchMap(([ , , role ]) => this.onlyOneCustomer.watch(undefined, {
      context: {
        headers: {
          [WO_CONTEXT_HEADER]: createUserContextHeader({ role }),
        },
      },
    }).valueChanges),
    map(
      mapApolloResultToActions({
        loading: appContextActions.onlyOneCustomerLoading(),
        data: ({
          customers: {
            totalCount,
            items,
          },
        }) => appContextActions.onlyOneCustomerLoaded({ customer: totalCount === 1 ? items[0]!.id : null }),
        error: () => appContextActions.onlyOneCustomerLoadingFailure({}),
      }),
    ),
    catchError(err => of(appContextActions.onlyOneCustomerLoadingFailure({ httpCode: httpCode(err) }))),
    repeat(),
  ));

  public readonly checkIfOnlyOneSiteAvailable$ = createEffect(() => this.actions$.pipe(
    ofType(
      authActions.detailsLoaded,
      appContextActions.customerSelected,
      appContextActions.onlyOneCustomerLoaded,
    ),
    concatLatestFrom(() => [
      this.store.select(selectShouldCheckOnlyOneSite),
      this.store.select(appContextFeature.selectCustomer),
      this.store.select(appContextFeature.selectRole),
    ]),
    filter(([ , shouldCheckOnlyOneSite ]) => shouldCheckOnlyOneSite),
    switchMap(([ , , customerId, role ]) => this.onlyOneSite.watch({ customerId }, {
      context: {
        headers: {
          [WO_CONTEXT_HEADER]: createUserContextHeader({ role, customer: customerId }),
        },
      },
    }).valueChanges),
    map(
      mapApolloResultToActions({
        loading: appContextActions.onlyOneSiteLoading(),
        data: ({ sites: { totalCount, items } }) => appContextActions.onlyOneSiteLoaded({ site: totalCount === 1 ? items[0]!.id : null }),
        error: () => appContextActions.onlyOneSiteLoadingFailure({}),
      }),
    ),
    catchError(err => of(appContextActions.onlyOneSiteLoadingFailure({ httpCode: httpCode(err) }))),
    repeat(),
  ));

  // public readonly checkIfOnlyOneServiceLocationsAvailable$ = createEffect(() => this.actions$.pipe(
  //   ofType(
  //     authActions.detailsLoaded,
  //     appContextActions.siteSelected,
  //     appContextActions.onlyOneSiteLoaded,
  //   ),
  //   concatLatestFrom(() => [
  //     this.store.select(selectShouldCheckOnlyOneServiceLocation),
  //     this.store.select(appContextFeature.selectSite),
  //     this.store.select(appContextFeature.selectRole),
  //   ]),
  //   filter(([ , shouldCheckOnlyOneServiceLocation ]) => shouldCheckOnlyOneServiceLocation),
  //   switchMap(([ , , siteId, role ]) => this.onlyOneServiceLocation.watch({ siteId }, {
  //     context: {
  //       headers: {
  //         [WO_CONTEXT_HEADER]: createUserContextHeader({ role, site: siteId }),
  //       },
  //     },
  //   }).valueChanges),
  //   map(
  //     mapApolloResultToActions({
  //       loading: appContextActions.onlyOneServiceLocationLoading(),
  //       data: ({ serviceLocations: { totalCount, items } }) => appContextActions.onlyOneServiceLocationLoaded({ serviceLocation: totalCount === 1 ? items[0]!.id : null }),
  //       error: () => appContextActions.onlyOneServiceLocationLoadingFailure({}),
  //     }),
  //   ),
  //   catchError(err => of(appContextActions.onlyOneServiceLocationLoadingFailure({ httpCode: httpCode(err) }))),
  //   repeat(),
  // ));

  public readonly checkMobileRes$ = createEffect(() => this.isMobileRes$.pipe(
    first(),
    map(isMobile => appContextActions.mobileDeviceDetected({ isMobile })),
  ));

  public readonly contextAccepted$ = createEffect(() => this.actions$.pipe(
    ofType(appContextActions.contextAccepted),
    tap(({ redirect }) => this.userContextService.close(redirect)),
  ), { dispatch: false });

  public readonly loadCountries$ = createEffect(() => this.store.select(appContextFeature.selectRole).pipe(
    filter(Boolean),
    switchMap((role) => this.countriesConfigGQL.watch(undefined, {
      fetchPolicy: 'no-cache',
      context: {
        headers: {
          [WO_CONTEXT_HEADER]: createUserContextHeader({ role }),
        },
      },
    }).valueChanges),
    mapApolloResult({
      loading: appContextActions.countriesLoading(),
      data: ({ countries }) => appContextActions.countriesLoaded({ countries }),
      error: err => appContextActions.countriesLoadingFailure({ httpCode: httpCode(err) }),
    }),
    repeat(),
  ));

  public readonly reset$ = createEffect(() => this.store.select(selectShouldResetCountries).pipe(
    filter(Boolean),
    map(() => appContextActions.resetCountries()),
  ));
}
